import type { Client } from '@ops/contracts'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { formatMetricValue } from '../../lib/format'
import { CHART } from '../../ui'
import { Vacio } from '../comunes'

/**
 * Antigüedad de la cola de implementación: barras horizontales de días
 * esperando, la más vieja arriba.
 *
 * Es la columna derecha de la tarjeta héroe de ops. Horizontal y no vertical
 * porque el eje que importa acá son los **nombres de las cuentas**, y verticales
 * quedarían rotados. El color de cada barra es el semáforo del aging, y el
 * número de días va además en el eje y en el tooltip: nunca solo el largo.
 */
export interface AgingChartProps {
  clientes: readonly Client[]
  /** Cuántas cuentas entran. Arriba de 8 la barra pierde legibilidad. */
  top?: number
  /** Días desde los que una espera pasa a crítica. */
  umbralCritico?: number
  /** Días desde los que una espera pide atención. */
  umbralAtencion?: number
}

export const UMBRAL_ESPERA_CRITICA = 60
export const UMBRAL_ESPERA_ATENCION = 30

export function severidadDeEspera(
  dias: number,
  critico = UMBRAL_ESPERA_CRITICA,
  atencion = UMBRAL_ESPERA_ATENCION,
): 'critical' | 'warning' | 'ok' {
  if (dias >= critico) return 'critical'
  if (dias >= atencion) return 'warning'
  return 'ok'
}

const TRAZO = {
  critical: CHART.perdida,
  warning: CHART.atencion,
  ok: CHART.serieSuave,
} as const

export function AgingChart({
  clientes,
  top = 6,
  umbralCritico = UMBRAL_ESPERA_CRITICA,
  umbralAtencion = UMBRAL_ESPERA_ATENCION,
}: AgingChartProps) {
  if (clientes.length === 0) {
    return (
      <Vacio icono={null}
        mensaje="No hay cuentas esperando implementación"
        detalle="Cuando entre una cuenta firmada sin implementar, su espera aparece acá."
      />
    )
  }

  const datos = [...clientes]
    .sort((a, b) => b.diasEnEstado - a.diasEnEstado)
    .slice(0, top)
    .map((c) => ({
      nombre: c.nombre,
      dias: c.diasEnEstado,
      severidad: severidadDeEspera(c.diasEnEstado, umbralCritico, umbralAtencion),
    }))

  return (
    <figure className="m-0">
      <figcaption className="rotulo mb-1.5">
        Días esperando · las {datos.length} más antiguas de {clientes.length}
      </figcaption>
      <div style={{ height: 28 * datos.length + 32 }} data-testid="aging-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={datos}
            layout="vertical"
            margin={{ top: 0, right: 32, bottom: 0, left: 4 }}
          >
            <CartesianGrid stroke={CHART.grid} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: CHART.eje }}
              stroke={CHART.grid}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="nombre"
              width={116}
              tick={{ fontSize: 11, fill: CHART.eje }}
              stroke={CHART.grid}
              tickLine={false}
            />
            <Tooltip
              formatter={(valor: number) => [
                `${formatMetricValue(valor, 'count')} días`,
                'Esperando',
              ]}
            />
            <Bar dataKey="dias" isAnimationActive={false}>
              {datos.map((fila) => (
                <Cell key={fila.nombre} fill={TRAZO[fila.severidad]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <figcaption className="mt-1 text-micro text-muted-strong">
        Ámbar a partir de {umbralAtencion} días, rojo a partir de{' '}
        {umbralCritico}. Los días también están en la tabla de la cola.
      </figcaption>
    </figure>
  )
}
